import { useEffect, useState } from "react";
import GlobalLoader from "./GlobalLoader";

interface AppBootLoaderProps {
  isLoading: boolean;
  /** Maximum time the loader can stay visible before forcing release (ms) */
  maxMs?: number;
  /** Minimum time the loader stays visible to avoid jarring flashes (ms) */
  minMs?: number;
  children: React.ReactNode;
}

/**
 * AppBootLoader
 * - Shows GlobalLoader while `isLoading` (auth) is true
 * - Additionally waits for fonts + first stable paint so the user never
 *   sees a half-rendered page (especially on mobile where lazy chunks
 *   and webfonts load slightly after the initial paint).
 * - Enforces minimum/maximum visible windows for a smooth feel.
 */
export default function AppBootLoader({
  isLoading,
  maxMs = 9000,
  minMs = 600,
  children,
}: AppBootLoaderProps) {
  const [authReady, setAuthReady] = useState(!isLoading);
  const [contentReady, setContentReady] = useState(false);
  const [minElapsed, setMinElapsed] = useState(false);
  const [forceRelease, setForceRelease] = useState(false);

  // Track auth readiness
  useEffect(() => {
    if (!isLoading) setAuthReady(true);
  }, [isLoading]);

  // Minimum visible window — avoids flicker
  useEffect(() => {
    const t = setTimeout(() => setMinElapsed(true), minMs);
    return () => clearTimeout(t);
  }, [minMs]);

  // Hard safety fallback — never get stuck
  useEffect(() => {
    const t = setTimeout(() => setForceRelease(true), maxMs);
    return () => clearTimeout(t);
  }, [maxMs]);

  // Wait for fonts + a couple of paint frames so layout has settled
  useEffect(() => {
    let cancelled = false;

    const settle = () => {
      if (cancelled) return;
      // Two RAFs ensure styles + layout from lazy chunks have flushed
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled) setContentReady(true);
        });
      });
    };

    const fontsPromise: Promise<unknown> =
      typeof document !== "undefined" && (document as any).fonts?.ready
        ? (document as any).fonts.ready
        : Promise.resolve();

    // Cap the font wait so a slow network can't trap the loader
    const fontsWithCap = Promise.race([
      fontsPromise,
      new Promise((res) => setTimeout(res, 2500)),
    ]);

    if (document.readyState === "complete") {
      fontsWithCap.then(settle);
    } else {
      const onLoad = () => fontsWithCap.then(settle);
      window.addEventListener("load", onLoad, { once: true });
      // Safety: if 'load' takes too long (lots of lazy assets), still settle
      const t = setTimeout(() => fontsWithCap.then(settle), 3500);
      return () => {
        cancelled = true;
        window.removeEventListener("load", onLoad);
        clearTimeout(t);
      };
    }

    return () => {
      cancelled = true;
    };
  }, []);

  const ready = forceRelease || (authReady && contentReady && minElapsed);

  if (!ready) return <GlobalLoader />;
  return <>{children}</>;
}
